import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import { useSetRecoilState } from "recoil";
import layoutConf from "../../config/layoutConf";
import notificationState from "../../states/notificationState";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import "react-reflex/styles.css";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import getSchema from "../../lib/api/getSchema";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteSingleRecordButton from "../input/DeleteSingleRecordButton";
import SaveToExcelButton from "../input/SaveToExcelButton";
import TkOutSourcingShipDoShipButton from "../input/TkOutSourcingShipDoShipButton";
import AccessControl from "../functional/AccessControl";
import { debounce } from "lodash";
import TkOutSourcingShipDetail from "./TkOutSourcingShipDetail";
import defineConf from "../../config/defineConf";

function TkOutSourcingShip(props) {
    const id = "TkOutSourcingShip";
    const table = "TkOutSourcingShip";
    const view = "TkVwOutSourcingShip";

    const { t } = useTranslation();

    const [height, setHeight] = useState(0);

    const [tkOutSourcingShipSchema, setTkOutSourcingShipSchema] = useState();
    const [tkOutSourcingShipGrid, setTkOutSourcingShipGrid] = useState();
    const [tkOutSourcingShipApi, setTkOutSourcingShipApi] = useState();
    const [tkOutSourcingShipData, setTkOutSourcingShipData] = useState();
    const [tkOutSourcingShipDataIsEmpty, setTkOutSourcingShipDataIsEmpty] = useState();
    const [tkOutSourcingShipDetailDataIsEmpty, setTkOutSourcingShipDetailDataIsEmpty] = useState();
    const [tkOutSourcingShipSelect, setTkOutSourcingShipSelect] = useState();

    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        (async () => {
            let schema = await getSchema(table);

            if (mounted) {
                setTkOutSourcingShipSchema(schema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (tkOutSourcingShipGrid) {
                tkOutSourcingShipGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingShipSelect(grid.selectedItems[0]);
                        } else {
                            setTkOutSourcingShipSelect();
                        }
                    }),
                    10
                );

                tkOutSourcingShipGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingShipSelect(grid.selectedItems[0]);
                        } else {
                            setTkOutSourcingShipSelect();
                        }
                    }),
                    10
                );
            }
        })();
    }, [tkOutSourcingShipGrid]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const onPartialFailure = (msg) => {
        notifyWarning(msg);
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.4}>
                    <BasicCrudScreenType
                        id={id}
                        headerTitle={t("term.outSourcingShip")}
                        table={table}
                        view={view}
                        onInitialized={(grid) => {
                            grid.selectionMode = "Row";
                            setTkOutSourcingShipGrid(grid);
                        }}
                        onApiInitialized={(api) => {
                            setTkOutSourcingShipApi(api);
                        }}
                        onLoadData={(data) => {
                            setTkOutSourcingShipData(data);
                            setTkOutSourcingShipDataIsEmpty(data.length < 1 ? true : false);
                        }}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl
                                    accessTarget={tkOutSourcingShipSchema && tkOutSourcingShipSchema.name}
                                    accessMethod="POST"
                                >
                                    <CreateRecordButton
                                        id={id}
                                        schema={tkOutSourcingShipSchema}
                                        refresh={tkOutSourcingShipApi}
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingShipSchema && tkOutSourcingShipSchema.name}
                                    accessMethod={
                                        tkOutSourcingShipSchema &&
                                        (tkOutSourcingShipSchema.updateWithPatch ? "PATCH" : "PUT")
                                    }
                                >
                                    <UpdateRecordButton
                                        id={id}
                                        schema={tkOutSourcingShipSchema}
                                        grid={tkOutSourcingShipGrid}
                                        refresh={tkOutSourcingShipApi}
                                        disabled={
                                            tkOutSourcingShipDataIsEmpty ||
                                            (tkOutSourcingShipSelect &&
                                                tkOutSourcingShipSelect?.outSourcingShipState ===
                                                    defineConf.outSourcingShipState.SHIP_COMPLETE)
                                        }
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingShipSchema && tkOutSourcingShipSchema.name}
                                    accessMethod="DELETE"
                                >
                                    <DeleteSingleRecordButton
                                        id={id}
                                        schema={tkOutSourcingShipSchema}
                                        grid={tkOutSourcingShipGrid}
                                        refresh={tkOutSourcingShipApi}
                                        makeItemName={(row) => {
                                            return `${t("columnName.outSourcingShipId")}: ${row.outSourcingShipId}`;
                                        }}
                                        color="secondary"
                                        disabled={
                                            tkOutSourcingShipDataIsEmpty ||
                                            !tkOutSourcingShipDetailDataIsEmpty ||
                                            (tkOutSourcingShipSelect &&
                                                tkOutSourcingShipSelect?.outSourcingShipState ===
                                                    defineConf.outSourcingShipState.SHIP_COMPLETE)
                                        }
                                        onSuccess={onSuccess}
                                        onFailure={onFailure}
                                        onPartialFailure={onPartialFailure}
                                    />
                                </AccessControl>

                                <TkOutSourcingShipDoShipButton
                                    schema={tkOutSourcingShipSchema}
                                    onSelectMst={tkOutSourcingShipSelect}
                                    grid={tkOutSourcingShipGrid}
                                    refresh={tkOutSourcingShipApi}
                                    disabled={
                                        tkOutSourcingShipDataIsEmpty ||
                                        tkOutSourcingShipDetailDataIsEmpty ||
                                        (tkOutSourcingShipSelect &&
                                            tkOutSourcingShipSelect?.outSourcingShipState ===
                                                defineConf.outSourcingShipState.SHIP_COMPLETE)
                                    }
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                />

                                <AccessControl
                                    accessTarget={tkOutSourcingShipSchema && tkOutSourcingShipSchema.name}
                                    accessMethod="SAVETOEXCEL"
                                >
                                    <SaveToExcelButton
                                        id={id}
                                        grid={tkOutSourcingShipGrid}
                                        view={view}
                                        color="primary"
                                        disabled={tkOutSourcingShipDataIsEmpty}
                                    />
                                </AccessControl>
                            </BasicCrudScreenActionItemLayout>
                        )}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement flex={0.6}>
                    {tkOutSourcingShipSelect ? (
                        <TkOutSourcingShipDetail
                            id={`${id}-detail`}
                            onSelectMst={tkOutSourcingShipSelect}
                            onDetailEmpty={(isDetailEmpty) => {
                                setTkOutSourcingShipDetailDataIsEmpty(isDetailEmpty);
                            }}
                        />
                    ) : (
                        <></>
                    )}
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default TkOutSourcingShip;
